import React, { useEffect, useMemo, useRef, useState } from 'react';
import { auth, db, storage } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteDoc,
  where,
  increment,
  Timestamp,
  orderBy,
  getCountFromServer,
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';

/*
  Editor.js
  ------------------------------------------------------------
  Nexora Editor panel.

  IMPORTANT:
  1. This page is designed for role === "Editor".
  2. Users are stored in the top-level "users" collection.
     The user document ID is the phoneNumber/uid, as used by Nexora.
  3. The optimized total-user counter is expected at:
       totalUser/stats
     with a numeric field:
       count
     Registration/deletion code should update this document.
  4. This page NEVER loads the complete users collection just to
     display the total. Searching a user reads only the requested
     user document.
  5. Reports are read from the "reports" collection. Because the
     exact report schema was not supplied yet, this file accepts
     common field names and can be adjusted later.
  6. Total-user initialization:
     - On the first Editor visit, if totalUser/stats is not initialized,
       users are counted once.
     - After that, the Editor reads only totalUser/stats.
     - Registration/deletion workflows should update totalUser/stats.
  7. Nexora Officials identity is stored in one fixed document:
       nexoraOfficials/main
     so the Editor can change the official name and profile picture.
     New official posts reuse that identity automatically.
  8. Report-a-problem submissions are stored in:
       reportProblems/{auto-id}
*/

const COLORS = {
  bg: '#050607',
  card: '#0a0d10',
  card2: '#0d1116',
  border: 'rgba(255,255,255,0.07)',
  text: '#eef3f8',
  muted: '#8e99a8',
  green: '#19c463',
  green2: '#0c7e3b',
  danger: '#ff5f6d',
  blue: '#4aa8ff',
  gold: '#e6b75a',
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  background: '#070a0d',
  border: `1px solid ${COLORS.border}`,
  color: COLORS.text,
  borderRadius: 14,
  padding: '13px 14px',
  outline: 'none',
  fontSize: 15,
};

const buttonStyle = {
  border: 'none',
  borderRadius: 13,
  padding: '11px 15px',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 14,
};

function firstValue(obj, keys, fallback = '') {
  for (const key of keys) {
    if (obj && obj[key] !== undefined && obj[key] !== null && obj[key] !== '') {
      return obj[key];
    }
  }
  return fallback;
}

function formatDate(value) {
  if (!value) return '';
  try {
    const date = value?.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString();
  } catch {
    return '';
  }
}

function normalizeRole(value) {
  return String(value || '').trim().toLowerCase();
}

function durationToMs(value, unit) {
  const n = Math.max(1, Number(value) || 1);
  const map = {
    seconds: 1000,
    minutes: 60 * 1000,
    hours: 60 * 60 * 1000,
    days: 24 * 60 * 60 * 1000,
  };
  return n * (map[unit] || map.minutes);
}

function toDateSafe(value) {
  if (!value) return null;
  try {
    const d = value?.toDate ? value.toDate() : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function normalizeDriveDownloadUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';

  // Google Drive sharing URL -> direct download endpoint when a file id is present.
  const match = raw.match(/(?:id=|\/d\/)([a-zA-Z0-9_-]{10,})/);
  if (match?.[1] && /drive\.google\.com|drive\.usercontent\.google\.com/i.test(raw)) {
    return `https://drive.usercontent.google.com/download?id=${match[1]}&export=download&confirm=t`;
  }
  return raw;
}

export default function Editor() {
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [editorUser, setEditorUser] = useState(null);

  const [totalUsers, setTotalUsers] = useState(0);
  const [totalLoading, setTotalLoading] = useState(true);

  const [searchNumber, setSearchNumber] = useState('');
  const [searchedUser, setSearchedUser] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchMessage, setSearchMessage] = useState('');

  const [editForm, setEditForm] = useState({});
  const [savingUser, setSavingUser] = useState(false);

  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [selectedReportUser, setSelectedReportUser] = useState(null);

  const [problems, setProblems] = useState([]);
  const [problemsLoading, setProblemsLoading] = useState(true);

  const [officialText, setOfficialText] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [publishMessage, setPublishMessage] = useState('');

  // Nexora Officials post composer. Existing editor/user/report logic is kept intact.
  const [officialPostType, setOfficialPostType] = useState('text');
  const [officialPlacement, setOfficialPlacement] = useState('popup');
  const [officialPostText, setOfficialPostText] = useState('');
  const [officialFile, setOfficialFile] = useState(null);
  const [officialFileUrl, setOfficialFileUrl] = useState('');
  const [officialUploading, setOfficialUploading] = useState(false);
  const [popupDelaySeconds, setPopupDelaySeconds] = useState(4);
  const [officialDurationValue, setOfficialDurationValue] = useState(1);
  const [officialDurationUnit, setOfficialDurationUnit] = useState('hours');
  const [officialVideoMuted, setOfficialVideoMuted] = useState(true);
  const [officialVideoVolume, setOfficialVideoVolume] = useState(1);
  const [officialAllowReactions, setOfficialAllowReactions] = useState(true);
  const [officialAllowComments, setOfficialAllowComments] = useState(true);
  const [officialAppName, setOfficialAppName] = useState('');
  const [officialAppUrl, setOfficialAppUrl] = useState('');
  const [officialPosts, setOfficialPosts] = useState([]);
  const [officialPostsLoading, setOfficialPostsLoading] = useState(false);
  const [moderationPost, setModerationPost] = useState(null);
  const [requireOtpForRegistration, setRequireOtpForRegistration] = useState(true);
  const [savingRegistrationSetting, setSavingRegistrationSetting] = useState(false);

  // Nexora Officials profile / identity editor. These values are stored in
  // nexoraOfficials/main and are reused by new official posts.
  const [officialBrandName, setOfficialBrandName] = useState('Nexora Officials');
  const [officialBrandImage, setOfficialBrandImage] = useState('/NexoraOfficials.png');
  const [showOfficialProfileEditor, setShowOfficialProfileEditor] = useState(false);
  const [officialBrandNameDraft, setOfficialBrandNameDraft] = useState('Nexora Officials');
  const [officialBrandImageDraft, setOfficialBrandImageDraft] = useState('/NexoraOfficials.png');
  const [officialBrandImageFile, setOfficialBrandImageFile] = useState(null);
  const [savingOfficialProfile, setSavingOfficialProfile] = useState(false);
  const officialBrandImageInputRef = useRef(null);

  const officialFileInputRef = useRef(null);

  const [activeSection, setActiveSection] = useState('users');

  /*
    ------------------------------------------------------------
    1) EDITOR ACCESS
    ------------------------------------------------------------
  */
  useEffect(() => {
    let cancelled = false;

    async function checkEditorAccess() {
      try {
        const firebaseUid = auth.currentUser?.uid;
        const storedUid =
          localStorage.getItem('nexora_user_uid') ||
          localStorage.getItem('uid') ||
          localStorage.getItem('currentUid') ||
          localStorage.getItem('userUid');

        // Nexora stores the user's phoneNumber/uid as the Firestore
        // document ID. Prefer that identity over Firebase Auth UID.
        const identity = storedUid || firebaseUid;

        if (!identity) {
          if (!cancelled) {
            setAllowed(false);
            setCheckingAccess(false);
          }
          return;
        }

        // First try the user's document directly.
        let userSnap = await getDoc(doc(db, 'users', identity));

        // Nexora commonly uses phoneNumber/uid as the document ID.
        // If Firebase Auth UID is different, look up only the matching uid.
        if (!userSnap.exists()) {
          const q = query(
            collection(db, 'users'),
            where('uid', '==', identity),
            limit(1)
          );
          const result = await getDocs(q);
          if (!result.empty) {
            userSnap = result.docs[0];
          }
        }

        if (!userSnap.exists()) {
          if (!cancelled) {
            setAllowed(false);
            setCheckingAccess(false);
          }
          return;
        }

        const data = userSnap.data();
        const isEditor = normalizeRole(data.role) === 'editor';

        if (!cancelled) {
          setEditorUser({ id: userSnap.id, ...data });
          setAllowed(isEditor);
          setCheckingAccess(false);
        }
      } catch (error) {
        console.error('Editor access check failed:', error);
        if (!cancelled) {
          setAllowed(false);
          setCheckingAccess(false);
        }
      }
    }

    checkEditorAccess();

    return () => {
      cancelled = true;
    };
  }, []);

  /*
    ------------------------------------------------------------
    2) REAL-TIME TOTAL USER COUNTER
    ------------------------------------------------------------
    Only one small document is listened to.
    It does NOT read the complete users collection.
  */
  useEffect(() => {
    if (!allowed) return undefined;

    let unsubscribe = null;
    let cancelled = false;

    const initializeAndListenTotal = async () => {
      const statsRef = doc(db, 'totalUser', 'stats');

      try {
        const existing = await getDoc(statsRef);

        // Count the users collection only once, before the aggregate is initialized.
        if (!existing.exists() || existing.data()?.initialized !== true) {
          const usersSnapshot = await getDocs(collection(db, 'users'));

          if (!cancelled) {
            await setDoc(
              statsRef,
              {
                count: usersSnapshot.size,
                initialized: true,
                initializedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            );
          }
        }

        unsubscribe = onSnapshot(
          statsRef,
          snapshot => {
            const data = snapshot.exists() ? snapshot.data() : {};
            const count = Number(data.count ?? data.total ?? 0);

            if (!cancelled) {
              setTotalUsers(Number.isFinite(count) ? count : 0);
              setTotalLoading(false);
            }
          },
          error => {
            console.error('totalUser listener failed:', error);
            if (!cancelled) setTotalLoading(false);
          }
        );
      } catch (error) {
        console.error('totalUser initialization failed:', error);
        if (!cancelled) setTotalLoading(false);
      }
    };

    initializeAndListenTotal();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [allowed]);

  useEffect(() => {
    if (!allowed) return undefined;
    getDoc(doc(db, 'appSettings', 'registration')).then(snapshot => {
      if (snapshot.exists()) setRequireOtpForRegistration(snapshot.data()?.requireOtp !== false);
    }).catch(() => {});
    return undefined;
  }, [allowed]);

  async function saveRegistrationSetting(value) {
    setSavingRegistrationSetting(true);
    try {
      await setDoc(doc(db, 'appSettings', 'registration'), {
        requireOtp: value,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setRequireOtpForRegistration(value);
      setPublishMessage(value ? 'OTP verification enabled for registration.' : 'OTP verification disabled for registration.');
    } catch (error) {
      setPublishMessage(error?.message || 'Could not update registration setting.');
    } finally {
      setSavingRegistrationSetting(false);
    }
  }

  /*
    ------------------------------------------------------------
    3) REPORTS
    ------------------------------------------------------------
    This is kept isolated so the report schema can be optimized
    later without touching the rest of the Editor screen.
  */
  useEffect(() => {
    if (!allowed) return undefined;

    const reportsRef = collection(db, 'reports');

    const unsubscribe = onSnapshot(
      reportsRef,
      snapshot => {
        const list = snapshot.docs.map(item => ({
          id: item.id,
          ...item.data(),
        }));

        list.sort((a, b) => {
          const aTime = firstValue(a, ['createdAt', 'timestamp', 'reportedAt'], 0);
          const bTime = firstValue(b, ['createdAt', 'timestamp', 'reportedAt'], 0);

          const aDate = aTime?.toDate ? aTime.toDate().getTime() : new Date(aTime).getTime();
          const bDate = bTime?.toDate ? bTime.toDate().getTime() : new Date(bTime).getTime();

          return (Number.isFinite(bDate) ? bDate : 0) - (Number.isFinite(aDate) ? aDate : 0);
        });

        setReports(list);
        setReportsLoading(false);
      },
      error => {
        console.error('reports listener failed:', error);
        setReportsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [allowed]);

  useEffect(() => {
    if (!allowed) return undefined;

    const problemsRef = collection(db, 'reportProblems');

    const unsubscribe = onSnapshot(
      problemsRef,
      snapshot => {
        const list = snapshot.docs.map(item => ({
          id: item.id,
          ...item.data(),
        }));

        list.sort((a, b) => {
          const aTime = firstValue(a, ['createdAt', 'timestamp'], 0);
          const bTime = firstValue(b, ['createdAt', 'timestamp'], 0);
          const aDate = aTime?.toDate ? aTime.toDate().getTime() : new Date(aTime).getTime();
          const bDate = bTime?.toDate ? bTime.toDate().getTime() : new Date(bTime).getTime();
          return (Number.isFinite(bDate) ? bDate : 0) - (Number.isFinite(aDate) ? aDate : 0);
        });

        setProblems(list);
        setProblemsLoading(false);
      },
      error => {
        console.error('reportProblems listener failed:', error);
        setProblemsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [allowed]);

  const reportGroups = useMemo(() => {
    const map = new Map();

    for (const report of reports) {
      const targetId = firstValue(
        report,
        ['reportedUid', 'reportedUserId', 'targetUid', 'targetUserId', 'userId', 'uid', 'phoneNumber'],
        'unknown'
      );

      const targetName = firstValue(
        report,
        ['reportedName', 'targetName', 'userName', 'fullName', 'name'],
        targetId
      );

      if (!map.has(String(targetId))) {
        map.set(String(targetId), {
          id: String(targetId),
          name: targetName,
          count: 0,
          reports: [],
        });
      }

      const group = map.get(String(targetId));
      group.count += 1;
      group.reports.push(report);
    }

    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [reports]);

  /*
    ------------------------------------------------------------
    4) USER SEARCH
    ------------------------------------------------------------
    Exact document lookup first.
    No automatic user list is rendered.
  */
  async function searchUser(event) {
    event?.preventDefault();

    const number = searchNumber.trim();

    if (!number) {
      setSearchMessage('Enter a phone number.');
      setSearchedUser(null);
      return;
    }

    setSearchLoading(true);
    setSearchMessage('');
    setSearchedUser(null);

    try {
      let snap = await getDoc(doc(db, 'users', number));

      // Fallback for installations where document ID differs.
      if (!snap.exists()) {
        const q = query(
          collection(db, 'users'),
          where('phoneNumber', '==', number),
          limit(1)
        );
        const result = await getDocs(q);

        if (!result.empty) {
          snap = result.docs[0];
        }
      }

      if (!snap.exists()) {
        setSearchMessage('No user found with this number.');
        return;
      }

      const data = snap.data();

      setSearchedUser({
        id: snap.id,
        ...data,
      });

      setEditForm({
        fullName: data.fullName ?? '',
        phoneNumber: data.phoneNumber ?? snap.id,
        password: data.password ?? '',
        uid: data.uid ?? snap.id,
        role: data.role ?? '',
        isActive: data.isActive !== false,
        canCreateChannel: data.canCreateChannel === true,
      });
    } catch (error) {
      console.error('User search failed:', error);
      setSearchMessage('Could not search this user.');
    } finally {
      setSearchLoading(false);
    }
  }

  function changeForm(key, value) {
    setEditForm(previous => ({
      ...previous,
      [key]: value,
    }));
  }

  /*
    ------------------------------------------------------------
    5) USER SAVE
    ------------------------------------------------------------
    Empty fields are allowed by design, as requested.
    Only fields shown in the Editor form are written.
  */
  async function saveUser() {
    if (!searchedUser) return;

    setSavingUser(true);
    setSearchMessage('');

    try {
      const oldDocId = searchedUser.id;
      const newDocId = String(editForm.uid || editForm.phoneNumber || oldDocId).trim();

      const payload = {
        fullName: editForm.fullName ?? '',
        phoneNumber: editForm.phoneNumber ?? '',
        password: editForm.password ?? '',
        uid: editForm.uid ?? '',
        role: editForm.role ?? '',
        isActive: editForm.isActive !== false,
        canCreateChannel: editForm.canCreateChannel === true,
      };

      // If UID/document ID changes, create the new document and remove old one.
      if (newDocId !== oldDocId) {
        await setDoc(doc(db, 'users', newDocId), payload);
        await deleteDoc(doc(db, 'users', oldDocId));
      } else {
        await updateDoc(doc(db, 'users', oldDocId), payload);
      }

      setSearchedUser({
        ...searchedUser,
        id: newDocId,
        ...payload,
      });

      setSearchNumber(newDocId);
      setSearchMessage('User updated successfully.');
    } catch (error) {
      console.error('User update failed:', error);
      setSearchMessage(error?.message || 'Could not update the user.');
    } finally {
      setSavingUser(false);
    }
  }

  /*
    ------------------------------------------------------------
    6) DEACTIVATE USER
    ------------------------------------------------------------
    We use isActive=false instead of deleting the account.
  */
  async function deactivateUser() {
    if (!searchedUser) return;

    const confirmed = await window.nexoraConfirm(
      `Deactivate ${searchedUser.phoneNumber || searchedUser.uid || searchedUser.id}?`
    );

    if (!confirmed) return;

    setSavingUser(true);

    try {
      await updateDoc(doc(db, 'users', searchedUser.id), {
        isActive: false,
      });

      setSearchedUser(previous => ({
        ...previous,
        isActive: false,
      }));

      setEditForm(previous => ({
        ...previous,
        isActive: false,
      }));

      setSearchMessage('User deactivated.');
    } catch (error) {
      console.error('Deactivate failed:', error);
      setSearchMessage(error?.message || 'Could not deactivate the user.');
    } finally {
      setSavingUser(false);
    }
  }

  /*
    ------------------------------------------------------------
    7) DELETE USER
    ------------------------------------------------------------
    The totalUser counter must be decremented by the secure
    registration/deletion backend logic. We deliberately do not
    let the browser modify the counter directly.
  */
  async function deleteUser() {
    if (!searchedUser) return;

    const confirmed = await window.nexoraConfirm(
      `Permanently delete user ${searchedUser.phoneNumber || searchedUser.uid || searchedUser.id}?`
    );

    if (!confirmed) return;

    setSavingUser(true);

    try {
      await deleteDoc(doc(db, 'users', searchedUser.id));

      await setDoc(
        doc(db, 'totalUser', 'stats'),
        {
          count: increment(-1),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setSearchedUser(null);
      setEditForm({});
      setSearchMessage('User deleted.');
    } catch (error) {
      console.error('Delete user failed:', error);
      setSearchMessage(error?.message || 'Could not delete the user.');
    } finally {
      setSavingUser(false);
    }
  }

  /*
    ------------------------------------------------------------
    8) NEXORA OFFICIALS
    ------------------------------------------------------------
    New official posts are stored in:
      officialUpdates/{postId}

    Supported post types:
      text, image, voice, video, apk

    placement:
      popup  -> dashboard can show after popupDelaySeconds and hide after duration
      banner -> dashboard can keep above the contract list until expiresAt

    Media files are uploaded to Firebase Storage under:
      officialUpdates/{postId}/media/{filename}

    View tracking:
      officialUpdates/{postId}/views/{userUid}
    The Dashboard should create/update the view document when a user actually
    sees the post. The Editor reads those child documents with an aggregate count.
  */
  async function loadOfficialProfile() {
    try {
      const profileSnap = await getDoc(doc(db, 'nexoraOfficials', 'main'));
      if (!profileSnap.exists()) return;

      const data = profileSnap.data() || {};
      const name = String(data.authorName || data.name || 'Nexora Officials').trim() || 'Nexora Officials';
      const image = String(data.profileImage || '/NexoraOfficials.png').trim() || '/NexoraOfficials.png';

      setOfficialBrandName(name);
      setOfficialBrandImage(image);
      setOfficialBrandNameDraft(name);
      setOfficialBrandImageDraft(image);
    } catch (error) {
      console.error('Nexora Officials profile load failed:', error);
    }
  }

  function openOfficialProfileEditor() {
    setOfficialBrandNameDraft(officialBrandName);
    setOfficialBrandImageDraft(officialBrandImage);
    setOfficialBrandImageFile(null);
    if (officialBrandImageInputRef.current) officialBrandImageInputRef.current.value = '';
    setShowOfficialProfileEditor(true);
  }

  function handleOfficialBrandImageChange(event) {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setPublishMessage('Please select a valid image file.');
      return;
    }

    setOfficialBrandImageFile(file);
    setOfficialBrandImageDraft(URL.createObjectURL(file));
  }

  async function saveOfficialProfile() {
    const name = officialBrandNameDraft.trim();
    if (!name) {
      setPublishMessage('Nexora Officials name cannot be empty.');
      return;
    }

    setSavingOfficialProfile(true);
    setPublishMessage('');

    try {
      let profileImage = officialBrandImageDraft || '/NexoraOfficials.png';

      if (officialBrandImageFile) {
        if (!storage) throw new Error('Firebase Storage is not exported from ../firebase.');

        const safeName = officialBrandImageFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `officialProfile/${Date.now()}_${safeName}`;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, officialBrandImageFile, {
          contentType: officialBrandImageFile.type || undefined,
        });
        profileImage = await getDownloadURL(fileRef);
      }

      await setDoc(
        doc(db, 'nexoraOfficials', 'main'),
        {
          authorName: name,
          name,
          profileImage,
          verified: true,
          subscriberCount: totalUsers,
          updatedAt: serverTimestamp(),
          active: true,
        },
        { merge: true }
      );

      setOfficialBrandName(name);
      setOfficialBrandImage(profileImage);
      setOfficialBrandNameDraft(name);
      setOfficialBrandImageDraft(profileImage);
      setOfficialBrandImageFile(null);
      setShowOfficialProfileEditor(false);
      setPublishMessage('Nexora Officials profile updated successfully.');
    } catch (error) {
      console.error('Nexora Officials profile update failed:', error);
      setPublishMessage(error?.message || 'Could not update Nexora Officials profile.');
    } finally {
      setSavingOfficialProfile(false);
    }
  }

  function resetOfficialComposer() {
    setOfficialPostType('text');
    setOfficialPlacement('popup');
    setOfficialPostText('');
    setOfficialFile(null);
    setOfficialFileUrl('');
    setPopupDelaySeconds(4);
    setOfficialDurationValue(1);
    setOfficialDurationUnit('hours');
    setOfficialVideoMuted(true);
    setOfficialVideoVolume(1);
    setOfficialAllowReactions(true);
    setOfficialAllowComments(true);
    setOfficialAppName('');
    setOfficialAppUrl('');
    if (officialFileInputRef.current) officialFileInputRef.current.value = '';
  }

  function handleOfficialFileChange(event) {
    const file = event.target.files?.[0] || null;
    setOfficialFile(file);
    setOfficialFileUrl('');
  }

  async function uploadOfficialMedia(postId) {
    if (!officialFile) return '';
    if (!storage) throw new Error('Firebase Storage is not exported from ../firebase.');

    const safeName = officialFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `officialUpdates/${postId}/media/${Date.now()}_${safeName}`;
    const fileRef = storageRef(storage, path);
    await uploadBytes(fileRef, officialFile, {
      contentType: officialFile.type || undefined,
    });
    return getDownloadURL(fileRef);
  }

  async function publishOfficialUpdate() {
    const type = officialPostType;
    const text = officialPostText.trim();
    const appName = officialAppName.trim();
    const appUrl = normalizeDriveDownloadUrl(officialAppUrl);

    if (type === 'text' && !text) {
      setPublishMessage('Write an update first.');
      return;
    }
    if (['image', 'voice', 'video'].includes(type) && !officialFile && !officialFileUrl.trim()) {
      setPublishMessage(`Select a ${type} file or paste its direct URL.`);
      return;
    }
    if (type === 'apk' && (!appName || !appUrl)) {
      setPublishMessage('Enter the app name and APK/Drive download link.');
      return;
    }

    setPublishing(true);
    setOfficialUploading(true);
    setPublishMessage('');

    try {
      const updateRef = doc(collection(db, 'officialUpdates'));
      const postId = updateRef.id;
      const durationMs = durationToMs(officialDurationValue, officialDurationUnit);
      const expiresAt = officialPlacement === 'banner'
        ? Timestamp.fromMillis(Date.now() + durationMs)
        : null;
      let mediaUrl = officialFileUrl.trim();

      if (officialFile) {
        mediaUrl = await uploadOfficialMedia(postId);
      }

      const payload = {
        authorName: officialBrandName,
        verified: true,
        profileImage: officialBrandImage,
        postType: type,
        placement: officialPlacement,
        text: type === 'text' ? text : '',
        mediaUrl: mediaUrl || '',
        mediaName: officialFile?.name || '',
        mediaMimeType: officialFile?.type || '',
        popupDelaySeconds: Math.max(0, Number(popupDelaySeconds) || 4),
        popupDurationValue: Math.max(1, Number(officialDurationValue) || 1),
        popupDurationUnit: officialDurationUnit,
        durationValue: Math.max(1, Number(officialDurationValue) || 1),
        durationUnit: officialDurationUnit,
        createdAt: serverTimestamp(),
        expiresAt,
        active: true,
        videoMuted: type === 'video' ? officialVideoMuted : false,
        videoVolume: type === 'video' ? Math.max(0, Math.min(1, Number(officialVideoVolume) || 0)) : 1,
        allowReactions: officialAllowReactions === true,
        allowComments: officialAllowComments === true,
        subscriberCount: totalUsers,
        viewCount: 0,
        commentCount: 0,
      };

      if (type === 'apk') {
        payload.appName = appName;
        payload.downloadUrl = appUrl;
        payload.text = '';
      }

      await setDoc(updateRef, payload);

      // Keep the old live document in sync for backward compatibility with
      // the existing Nexora Officials UI. The new Dashboard can use officialUpdates.
      if (type === 'text') {
        await setDoc(
          doc(db, 'nexoraOfficials', 'main'),
          {
            text,
            authorName: officialBrandName,
            verified: true,
            profileImage: officialBrandImage,
            subscriberCount: totalUsers,
            updatedAt: serverTimestamp(),
            active: true,
          },
          { merge: true }
        );
      }

      setPublishMessage('Nexora Officials post published successfully.');
      resetOfficialComposer();
      await loadOfficialPosts();
    } catch (error) {
      console.error('Official post publish failed:', error);
      setPublishMessage(error?.message || 'Could not publish the official post.');
    } finally {
      setPublishing(false);
      setOfficialUploading(false);
    }
  }

  async function toggleOfficialPostSetting(postId, field, value) {
    try {
      await updateDoc(doc(db, 'officialUpdates', postId), { [field]: value });
      setOfficialPosts(previous => previous.map(post => post.id === postId ? { ...post, [field]: value } : post));
    } catch (error) {
      setPublishMessage(error?.message || 'Could not update post settings.');
    }
  }

  async function loadOfficialPosts() {
    if (!allowed) return;
    setOfficialPostsLoading(true);
    try {
      const snap = await getDocs(collection(db, 'officialUpdates'));
      const raw = snap.docs
        .map(item => ({ id: item.id, ...item.data() }))
        .sort((a, b) => {
          const at = toDateSafe(a.createdAt)?.getTime() || 0;
          const bt = toDateSafe(b.createdAt)?.getTime() || 0;
          return bt - at;
        })
        .slice(0, 100);

      const withCounts = await Promise.all(
        raw.map(async post => {
          try {
            const countSnap = await getCountFromServer(
              collection(db, 'officialUpdates', post.id, 'views')
            );
            return { ...post, viewCount: countSnap.data().count };
          } catch (error) {
            // Existing posts may not have a views subcollection yet.
            return { ...post, viewCount: Number(post.viewCount || 0) };
          }
        })
      );

      setOfficialPosts(withCounts);
    } catch (error) {
      console.error('Official posts load failed:', error);
      setPublishMessage('Could not load official post history.');
    } finally {
      setOfficialPostsLoading(false);
    }
  }

  async function refreshOfficialPostViews() {
    await loadOfficialPosts();
  }

  async function deleteOfficialPost(post) {
    if (!await window.nexoraConfirm(`Delete this Nexora Officials post?\n\n${post.appName || post.text || post.postType || 'Post'}`)) return;
    try {
      await deleteDoc(doc(db, 'officialUpdates', post.id));
      setOfficialPosts(previous => previous.filter(item => item.id !== post.id));
      setPublishMessage('Official post deleted.');
    } catch (error) {
      console.error('Official post delete failed:', error);
      setPublishMessage(error?.message || 'Could not delete the official post.');
    }
  }

  useEffect(() => {
    if (!allowed || activeSection !== 'officials') return undefined;

    const officialRef = doc(db, 'nexoraOfficials', 'main');

    const unsubscribe = onSnapshot(
      officialRef,
      snapshot => {
        setOfficialText(snapshot.exists() ? (snapshot.data().text || '') : '');
      },
      error => console.error('Nexora Officials legacy listener failed:', error)
    );

    loadOfficialProfile();
    loadOfficialPosts();

    return () => unsubscribe();
  }, [allowed, activeSection]);

  if (checkingAccess) {
    return (
      <div style={styles.fullScreen}>
        <div style={styles.loaderCard}>
          <div style={styles.spinner} />
          <div style={{ marginTop: 14, color: COLORS.muted }}>
            Checking Editor access...
          </div>
        </div>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div style={styles.fullScreen}>
        <div style={styles.deniedCard}>
          <div style={styles.deniedIcon}>!</div>
          <h2 style={{ margin: '0 0 8px', color: COLORS.text }}>
            Editor Access Required
          </h2>
          <p style={{ margin: 0, color: COLORS.muted, lineHeight: 1.6 }}>
            This page is available only to users whose Firestore role is
            <strong style={{ color: COLORS.green }}> Editor</strong>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <style>{`
        * { box-sizing: border-box; }
        html, body, #root { margin: 0; min-height: 100%; background: ${COLORS.bg}; }
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
        button, input, textarea, select { font-family: inherit; }
        button:active { transform: scale(.98); }
        .editor-scroll::-webkit-scrollbar { width: 5px; }
        .editor-scroll::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,.12);
          border-radius: 10px;
        }
        .editor-input:focus {
          border-color: rgba(25,196,99,.55) !important;
          box-shadow: 0 0 0 3px rgba(25,196,99,.08);
        }
        .editor-scroll {
          scrollbar-width: thin;
          -webkit-overflow-scrolling: touch;
        }
        @media (max-width: 640px) {
          .editor-scroll { padding-bottom: 120px !important; }
          .official-composer-grid { grid-template-columns: 1fr !important; }
          .official-inline-fields { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <header style={styles.header}>
        <div>
          <div style={styles.brand}>Nexora</div>
          <div style={styles.subtitle}>
            Editor Panel
            <span style={styles.editorBadge}>EDITOR</span>
          </div>
        </div>

        <div style={styles.headerUser}>
          <div style={styles.avatar}>
            {(editorUser?.fullName || 'E').charAt(0).toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={styles.headerName}>
              {editorUser?.fullName || 'Editor'}
            </div>
            <div style={styles.headerRole}>Editor</div>
          </div>
        </div>
      </header>

      <main className="editor-scroll" style={styles.content}>
        <section style={styles.totalCard}>
          <div>
            <div style={styles.eyebrow}>TOTAL USERS</div>
            <div style={styles.totalNumber}>
              {totalLoading ? '—' : totalUsers.toLocaleString()}
            </div>
            <div style={styles.totalHint}>
              Live counter from <code style={styles.code}>totalUser/stats</code>
            </div>
          </div>

          <div style={styles.totalIcon}>👥</div>
        </section>

        <nav style={styles.tabs}>
          <Tab
            active={activeSection === 'users'}
            onClick={() => setActiveSection('users')}
            icon="⌕"
            label="Users"
          />
          <Tab
            active={activeSection === 'reports'}
            onClick={() => setActiveSection('reports')}
            icon="⚑"
            label="User Reports"
            count={reportGroups.length}
          />
          <Tab
            active={activeSection === 'problems'}
            onClick={() => setActiveSection('problems')}
            icon="!"
            label="Report Problems"
            count={problems.length}
          />
          <Tab
            active={activeSection === 'officials'}
            onClick={() => setActiveSection('officials')}
            icon="✓"
            label="Nexora Officials"
          />
        </nav>

        {activeSection === 'users' && (
          <section style={styles.section}>
            <SectionTitle
              title="Find User"
              subtitle="Search by the user's phone number / UID"
              icon="⌕"
            />

            <form onSubmit={searchUser} style={styles.searchRow}>
              <input
                className="editor-input"
                value={searchNumber}
                onChange={e => setSearchNumber(e.target.value)}
                placeholder="+8801XXXXXXXXX"
                style={inputStyle}
                inputMode="tel"
              />
              <button
                type="submit"
                style={{
                  ...buttonStyle,
                  background: COLORS.green,
                  color: '#001b0b',
                  minWidth: 96,
                }}
                disabled={searchLoading}
              >
                {searchLoading ? '...' : 'Search'}
              </button>
            </form>

            <label style={{ ...styles.permissionCard, marginTop: 14 }}>
              <input
                type="checkbox"
                checked={requireOtpForRegistration}
                disabled={savingRegistrationSetting}
                onChange={event => saveRegistrationSetting(event.target.checked)}
              />
              <span>
                <strong style={{ display: 'block', color: COLORS.text }}>Require OTP during registration</strong>
                <small style={{ display: 'block', color: COLORS.muted, marginTop: 4 }}>
                  On: registration opens OtpVerification.js. Off: account is created directly and opens Login.js.
                </small>
              </span>
            </label>

            {searchMessage && (
              <div style={styles.messageBox}>{searchMessage}</div>
            )}

            {!searchedUser && !searchLoading && (
              <div style={styles.emptyCard}>
                <div style={styles.emptyIcon}>⌕</div>
                <div style={{ color: COLORS.text, fontWeight: 700 }}>
                  No user loaded
                </div>
                <div style={{ color: COLORS.muted, marginTop: 5 }}>
                  Enter a number above. Users will not automatically fill this screen.
                </div>
              </div>
            )}

            {searchedUser && (
              <div style={styles.userCard}>
                <div style={styles.userCardHeader}>
                  <div style={styles.profileBlock}>
                    <img
                      src={
                        searchedUser.photoURL ||
                        searchedUser.photoUrl ||
                        '/favicon.png'
                      }
                      alt=""
                      style={styles.profileImage}
                      onError={e => {
                        e.currentTarget.src = '/favicon.png';
                      }}
                    />

                    <div style={{ minWidth: 0 }}>
                      <div style={styles.profileName}>
                        {searchedUser.fullName || 'Unnamed user'}
                      </div>
                      <div style={styles.profileNumber}>
                        {searchedUser.phoneNumber || searchedUser.uid || searchedUser.id}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      ...styles.statusPill,
                      color: editForm.isActive ? COLORS.green : COLORS.danger,
                      borderColor: editForm.isActive
                        ? 'rgba(25,196,99,.25)'
                        : 'rgba(255,95,109,.25)',
                    }}
                  >
                    {editForm.isActive ? 'Active' : 'Deactivated'}
                  </div>
                </div>

                <div style={styles.formGrid}>
                  <Field
                    label="Name"
                    value={editForm.fullName}
                    onChange={value => changeForm('fullName', value)}
                  />

                  <Field
                    label="Phone Number"
                    value={editForm.phoneNumber}
                    onChange={value => changeForm('phoneNumber', value)}
                    inputMode="tel"
                  />

                  <Field
                    label="Password"
                    value={editForm.password}
                    onChange={value => changeForm('password', value)}
                  />

                  <Field
                    label="UID"
                    value={editForm.uid}
                    onChange={value => changeForm('uid', value)}
                  />

                  <div>
                    <label style={styles.label}>Role</label>
                    <input
                      className="editor-input"
                      value={editForm.role}
                      onChange={e => changeForm('role', e.target.value)}
                      placeholder="Premium / Admin / Editor / empty"
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={styles.label}>Account Status</label>
                    <select
                      className="editor-input"
                      value={editForm.isActive ? 'active' : 'inactive'}
                      onChange={e =>
                        changeForm('isActive', e.target.value === 'active')
                      }
                      style={inputStyle}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Deactivated</option>
                    </select>
                  </div>

                  <label style={styles.permissionCard}>
                    <input
                      type="checkbox"
                      checked={editForm.canCreateChannel === true}
                      onChange={e => changeForm('canCreateChannel', e.target.checked)}
                    />
                    <span>
                      <strong style={{ display: 'block', color: COLORS.text }}>Allow Channel Creation</strong>
                      <small style={{ display: 'block', color: COLORS.muted, marginTop: 4 }}>
                        Off by default. Only enabled users can create channels.
                      </small>
                    </span>
                  </label>
                </div>

                <div style={styles.actionRow}>
                  <button
                    onClick={saveUser}
                    disabled={savingUser}
                    style={{
                      ...buttonStyle,
                      background: COLORS.green,
                      color: '#001b0b',
                    }}
                  >
                    {savingUser ? 'Saving...' : 'Save Changes'}
                  </button>

                  <button
                    onClick={deactivateUser}
                    disabled={savingUser}
                    style={{
                      ...buttonStyle,
                      background: 'rgba(255,185,70,.10)',
                      color: COLORS.gold,
                      border: '1px solid rgba(255,185,70,.18)',
                    }}
                  >
                    Deactivate
                  </button>

                  <button
                    onClick={deleteUser}
                    disabled={savingUser}
                    style={{
                      ...buttonStyle,
                      background: 'rgba(255,95,109,.10)',
                      color: COLORS.danger,
                      border: '1px solid rgba(255,95,109,.18)',
                    }}
                  >
                    Delete ID
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {activeSection === 'reports' && (
          <section style={styles.section}>
            <SectionTitle
              title="User Reports"
              subtitle="Users grouped by the number of reports received"
              icon="⚑"
            />

            {reportsLoading ? (
              <LoadingCard text="Loading reports..." />
            ) : reportGroups.length === 0 ? (
              <EmptyCard text="No reports found." />
            ) : (
              <div style={styles.list}>
                {reportGroups.map(group => (
                  <button
                    key={group.id}
                    onClick={() => setSelectedReportUser(group)}
                    style={styles.reportRow}
                  >
                    <div style={styles.reportAvatar}>
                      {(group.name || '?').charAt(0).toUpperCase()}
                    </div>

                    <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                      <div style={styles.reportName}>{group.name}</div>
                      <div style={styles.reportUid}>{group.id}</div>
                    </div>

                    <div style={styles.reportCount}>{group.count}</div>
                  </button>
                ))}
              </div>
            )}

            {selectedReportUser && (
              <div style={styles.modalOverlay} onClick={() => setSelectedReportUser(null)}>
                <div
                  style={styles.modal}
                  onClick={e => e.stopPropagation()}
                >
                  <div style={styles.modalHeader}>
                    <div>
                      <div style={styles.modalTitle}>
                        {selectedReportUser.name}
                      </div>
                      <div style={styles.modalSub}>
                        {selectedReportUser.count} report(s)
                      </div>
                    </div>

                    <button
                      onClick={() => setSelectedReportUser(null)}
                      style={styles.closeButton}
                    >
                      ×
                    </button>
                  </div>

                  <div className="editor-scroll" style={styles.reportDetailList}>
                    {selectedReportUser.reports.map((report, index) => {
                      const reporter = firstValue(
                        report,
                        ['reporterName', 'fromName', 'senderName', 'reportedBy'],
                        'Unknown reporter'
                      );

                      const message = firstValue(
                        report,
                        ['message', 'sms', 'text', 'reason', 'description'],
                        'No report message'
                      );

                      return (
                        <div key={report.id || index} style={styles.detailCard}>
                          <div style={styles.detailTop}>
                            <strong>{reporter}</strong>
                            <span>
                              {formatDate(
                                firstValue(report, [
                                  'createdAt',
                                  'timestamp',
                                  'reportedAt',
                                ])
                              )}
                            </span>
                          </div>

                          <div style={styles.detailMessage}>{message}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        {activeSection === 'problems' && (
          <section style={styles.section}>
            <SectionTitle
              title="Report Problems"
              subtitle="Total problem reports and the submitted messages"
              icon="!"
            />

            <div style={styles.problemTotal}>
              <div>
                <div style={styles.eyebrow}>TOTAL REPORT PROBLEMS</div>
                <div style={styles.problemNumber}>{problems.length}</div>
              </div>
              <div style={styles.problemIcon}>!</div>
            </div>

            <div style={{ marginTop: 14 }}>
              {problemsLoading ? (
                <LoadingCard text="Loading report problems..." />
              ) : problems.length === 0 ? (
                <EmptyCard text="No report problems found." />
              ) : (
                <div style={styles.list}>
                  {problems.map(report => {
                    const reporter = firstValue(
                      report,
                      ['reporterName', 'fromName', 'senderName', 'reportedBy'],
                      'Unknown user'
                    );

                    const message = firstValue(
                      report,
                      ['message', 'sms', 'text', 'reason', 'description'],
                      'No message'
                    );

                    return (
                      <div key={report.id} style={styles.problemRow}>
                        <div style={styles.problemAvatar}>
                          {reporter.charAt(0).toUpperCase()}
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={styles.reportName}>{reporter}</div>
                          <div style={styles.problemMessage}>{message}</div>
                          <div style={styles.problemDate}>
                            {formatDate(
                              firstValue(report, [
                                'createdAt',
                                'timestamp',
                                'reportedAt',
                              ])
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        )}

        {activeSection === 'officials' && (
          <section style={styles.section}>
            <SectionTitle
              title="Nexora Officials"
              subtitle="Publish text, PNG photo, voice, video or APK download posts"
              icon="✓"
            />

            <div style={styles.officialCard}>
              <div style={styles.officialHeader}>
                <img
                  src={officialBrandImage || '/NexoraOfficials.png'}
                  alt={officialBrandName}
                  style={styles.officialImage}
                  onError={e => {
                    if (!e.currentTarget.dataset.fallback) {
                      e.currentTarget.dataset.fallback = '1';
                      e.currentTarget.src = '/NexoraOfficials.png';
                    }
                  }}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={styles.officialName}>
                    {officialBrandName}
                    <span style={styles.verified}>✓</span>
                  </div>
                  <div style={styles.officialMeta}>
                    {totalUsers.toLocaleString()} subscribers
                  </div>
                </div>
                <button
                  type="button"
                  onClick={openOfficialProfileEditor}
                  style={styles.officialEditButton}
                  title="Edit Nexora Officials name and profile picture"
                >
                  ✎ Edit
                </button>
              </div>

              {showOfficialProfileEditor && (
                <div style={styles.officialProfileEditor}>
                  <div style={styles.officialProfileEditorTitle}>Edit Nexora Officials</div>

                  <div style={styles.officialProfilePreviewRow}>
                    <img
                      src={officialBrandImageDraft || '/NexoraOfficials.png'}
                      alt="Preview"
                      style={styles.officialProfilePreview}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <label style={styles.label}>Profile Picture</label>
                      <input
                        ref={officialBrandImageInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleOfficialBrandImageChange}
                        style={inputStyle}
                      />
                      <div style={{ marginTop: 6, color: COLORS.muted, fontSize: 11 }}>
                        PNG/JPG/WebP image. The selected image will be uploaded to Firebase Storage.
                      </div>
                    </div>
                  </div>

                  <label style={styles.label}>Official Name</label>
                  <input
                    className="editor-input"
                    value={officialBrandNameDraft}
                    onChange={e => setOfficialBrandNameDraft(e.target.value)}
                    placeholder="Nexora Officials"
                    style={inputStyle}
                  />

                  <div style={styles.officialProfileActions}>
                    <button
                      type="button"
                      onClick={() => setShowOfficialProfileEditor(false)}
                      disabled={savingOfficialProfile}
                      style={{ ...buttonStyle, background: '#151a1f', color: COLORS.muted }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={saveOfficialProfile}
                      disabled={savingOfficialProfile}
                      style={{ ...buttonStyle, background: COLORS.green, color: '#001b0b' }}
                    >
                      {savingOfficialProfile ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}

              <div className="official-composer-grid" style={styles.composerGrid}>
                <div>
                  <label style={styles.label}>Post Type</label>
                  <select
                    className="editor-input"
                    value={officialPostType}
                    onChange={e => setOfficialPostType(e.target.value)}
                    style={inputStyle}
                  >
                    <option value="text">Text</option>
                    <option value="image">PNG Photo</option>
                    <option value="voice">Voice</option>
                    <option value="video">Video</option>
                    <option value="apk">APK Download</option>
                  </select>
                </div>

                <div>
                  <label style={styles.label}>Show On Dashboard</label>
                  <select
                    className="editor-input"
                    value={officialPlacement}
                    onChange={e => setOfficialPlacement(e.target.value)}
                    style={inputStyle}
                  >
                    <option value="popup">Popup</option>
                    <option value="banner">Above Contract List</option>
                  </select>
                </div>

                <div>
                  <label style={styles.label}>Popup Delay (seconds)</label>
                  <input
                    className="editor-input"
                    type="number"
                    min="0"
                    step="1"
                    value={popupDelaySeconds}
                    onChange={e => setPopupDelaySeconds(e.target.value)}
                    style={inputStyle}
                    disabled={officialPlacement !== 'popup'}
                  />
                </div>

                <div>
                  <label style={styles.label}>Post Duration</label>
                  <div className="official-inline-fields" style={styles.inlineFields}>
                    <input
                      className="editor-input"
                      type="number"
                      min="1"
                      step="1"
                      value={officialDurationValue}
                      onChange={e => setOfficialDurationValue(e.target.value)}
                      style={{ ...inputStyle, minWidth: 0 }}
                    />
                    <select
                      className="editor-input"
                      value={officialDurationUnit}
                      onChange={e => setOfficialDurationUnit(e.target.value)}
                      style={{ ...inputStyle, minWidth: 0 }}
                    >
                      <option value="seconds">Seconds</option>
                      <option value="minutes">Minutes</option>
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                    </select>
                  </div>
                </div>
              </div>

              {officialPostType === 'text' && (
                <>
                  <label style={{ ...styles.label, marginTop: 16 }}>Text</label>
                  <textarea
                    className="editor-input"
                    value={officialPostText}
                    onChange={e => setOfficialPostText(e.target.value)}
                    placeholder="Write an official Nexora announcement..."
                    rows={7}
                    style={{
                      ...inputStyle,
                      resize: 'vertical',
                      lineHeight: 1.55,
                    }}
                  />
                </>
              )}

              {['image', 'voice', 'video'].includes(officialPostType) && (
                <div style={{ marginTop: 16 }}>
                  <label style={styles.label}>
                    {officialPostType === 'image' ? 'PNG Photo' : officialPostType === 'voice' ? 'Voice File' : 'Video File'}
                  </label>
                  <input
                    ref={officialFileInputRef}
                    className="editor-input"
                    type="file"
                    accept={
                      officialPostType === 'image'
                        ? 'image/png'
                        : officialPostType === 'voice'
                          ? 'audio/*'
                          : 'video/*'
                    }
                    onChange={handleOfficialFileChange}
                    style={inputStyle}
                  />
                  <div style={{ marginTop: 8, color: COLORS.muted, fontSize: 12 }}>
                    Optional: paste a direct media URL instead.
                  </div>
                  <input
                    className="editor-input"
                    value={officialFileUrl}
                    onChange={e => setOfficialFileUrl(e.target.value)}
                    placeholder="https://..."
                    style={{ ...inputStyle, marginTop: 8 }}
                  />
                </div>
              )}

              {officialPostType === 'video' && (
                <div style={styles.videoControls}>
                  <label style={styles.checkRow}>
                    <input
                      type="checkbox"
                      checked={officialVideoMuted}
                      onChange={e => setOfficialVideoMuted(e.target.checked)}
                    />
                    <span>Start muted / autoplay silently</span>
                  </label>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <label style={styles.label}>Video Volume</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={officialVideoVolume}
                      onChange={e => setOfficialVideoVolume(Number(e.target.value))}
                      style={{ width: '100%' }}
                    />
                    <div style={{ color: COLORS.muted, fontSize: 11 }}>
                      {Math.round(Number(officialVideoVolume) * 100)}%
                    </div>
                  </div>
                </div>
              )}

              <div style={styles.permissionGrid}>
                <label style={styles.permissionCard}>
                  <input
                    type="checkbox"
                    checked={officialAllowReactions}
                    onChange={e => setOfficialAllowReactions(e.target.checked)}
                  />
                  <span><strong style={{ display: 'block', color: COLORS.text }}>Allow reactions</strong><small style={{ color: COLORS.muted }}>Users can react and see the total.</small></span>
                </label>
                <label style={styles.permissionCard}>
                  <input
                    type="checkbox"
                    checked={officialAllowComments}
                    onChange={e => setOfficialAllowComments(e.target.checked)}
                  />
                  <span><strong style={{ display: 'block', color: COLORS.text }}>Allow comments</strong><small style={{ color: COLORS.muted }}>Everyone can read and add comments.</small></span>
                </label>
              </div>

              {officialPostType === 'apk' && (
                <div style={styles.apkBox}>
                  <label style={styles.label}>App Name</label>
                  <input
                    className="editor-input"
                    value={officialAppName}
                    onChange={e => setOfficialAppName(e.target.value)}
                    placeholder="Nexora App"
                    style={inputStyle}
                  />
                  <label style={{ ...styles.label, marginTop: 12 }}>APK / Drive Link</label>
                  <input
                    className="editor-input"
                    value={officialAppUrl}
                    onChange={e => setOfficialAppUrl(e.target.value)}
                    placeholder="Google Drive share link or direct APK URL"
                    style={inputStyle}
                  />
                  <div style={{ marginTop: 8, color: COLORS.muted, fontSize: 12, lineHeight: 1.45 }}>
                    The Dashboard receives the app name and download URL only. A common Google Drive sharing URL is converted to a direct download endpoint when possible.
                  </div>
                </div>
              )}

              <div style={styles.officialBottom}>
                <span style={{ color: COLORS.muted, fontSize: 12, lineHeight: 1.45 }}>
                  Card layout, 4-second popup entry, autoplay behavior and placement above the Contract List are controlled by Dashboard.js using the fields saved here.
                </span>

                <button
                  onClick={publishOfficialUpdate}
                  disabled={publishing || officialUploading}
                  style={{
                    ...buttonStyle,
                    background: COLORS.green,
                    color: '#001b0b',
                    minWidth: 170,
                  }}
                >
                  {officialUploading ? 'Uploading...' : publishing ? 'Publishing...' : 'Publish Post'}
                </button>
              </div>

              {publishMessage && (
                <div style={styles.messageBox}>{publishMessage}</div>
              )}
            </div>

            <div style={styles.postHistoryCard}>
              <div style={styles.historyHeader}>
                <div>
                  <div style={styles.cardTitle}>Published Official Posts</div>
                  <div style={styles.historyHint}>See how many users viewed each post.</div>
                </div>
                <button
                  onClick={refreshOfficialPostViews}
                  disabled={officialPostsLoading}
                  style={{
                    ...buttonStyle,
                    background: 'rgba(25,196,99,.10)',
                    color: COLORS.green,
                    border: '1px solid rgba(25,196,99,.18)',
                  }}
                >
                  {officialPostsLoading ? 'Loading...' : 'Refresh Views'}
                </button>
              </div>

              {officialPostsLoading ? (
                <LoadingCard text="Loading official posts..." />
              ) : officialPosts.length === 0 ? (
                <EmptyCard text="No official posts published yet." />
              ) : (
                <div style={styles.list}>
                  {officialPosts.map(post => {
                    const expires = toDateSafe(post.expiresAt);
                    const expired = expires ? expires.getTime() <= Date.now() : false;
                    const label = post.postType === 'apk'
                      ? `APK • ${post.appName || 'App'}`
                      : `${post.postType || 'text'} • ${post.placement || 'popup'}`;
                    return (
                      <div key={post.id} style={styles.officialPostRow}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={styles.postRowTop}>
                            <strong>{label}</strong>
                            <span style={{ color: expired ? COLORS.danger : COLORS.green }}>
                              {expired ? 'Expired' : 'Active'}
                            </span>
                          </div>
                          <div style={styles.postRowText}>
                            {post.postType === 'apk'
                              ? (post.appName || 'APK Download')
                              : post.text || post.mediaName || 'Media post'}
                          </div>
                          <div style={styles.postRowMeta}>
                            {post.placement === 'popup'
                              ? `Popup after ${post.popupDelaySeconds || 0}s • shows for ${post.popupDurationValue || post.durationValue || 1} ${post.popupDurationUnit || post.durationUnit || 'seconds'} per user`
                              : `Above Contract List • ${post.durationValue || 1} ${post.durationUnit || 'hours'} global duration • Expires ${formatDate(post.expiresAt) || '—'}`}
                          </div>
                        </div>
                        <div style={styles.viewCountBox}>
                          <div style={styles.viewNumber}>{Number(post.viewCount || 0).toLocaleString()}</div>
                          <div style={styles.viewLabel}>Views</div>
                        </div>
                        <div style={styles.postPermissions}>
                          <label><input type="checkbox" checked={post.allowReactions !== false} onChange={e => toggleOfficialPostSetting(post.id, 'allowReactions', e.target.checked)} /> React</label>
                          <label><input type="checkbox" checked={post.allowComments !== false} onChange={e => toggleOfficialPostSetting(post.id, 'allowComments', e.target.checked)} /> Comment</label>
                        </div>
                        <button
                          onClick={() => setModerationPost(post)}
                          style={{
                            ...buttonStyle,
                            background: 'rgba(74,168,255,.10)',
                            color: COLORS.blue,
                            border: '1px solid rgba(74,168,255,.18)',
                          }}
                        >
                          Comments
                        </button>
                        <button
                          onClick={() => deleteOfficialPost(post)}
                          style={{
                            ...buttonStyle,
                            background: 'rgba(255,95,109,.10)',
                            color: COLORS.danger,
                            border: '1px solid rgba(255,95,109,.18)',
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        )}

      </main>
      {moderationPost && (
        <CommentModerationModal
          post={moderationPost}
          onClose={() => setModerationPost(null)}
          onMessage={setPublishMessage}
        />
      )}
    </div>
  );
}

function Tab({ active, onClick, icon, label, count }) {
  return (
    <button
      onClick={onClick}
      style={{
        ...styles.tab,
        background: active ? 'rgba(25,196,99,.12)' : COLORS.card,
        borderColor: active
          ? 'rgba(25,196,99,.28)'
          : COLORS.border,
        color: active ? COLORS.green : COLORS.muted,
      }}
    >
      <span style={styles.tabIcon}>{icon}</span>
      <span>{label}</span>
      {count !== undefined && (
        <span
          style={{
            ...styles.tabCount,
            color: active ? COLORS.green : COLORS.muted,
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function SectionTitle({ title, subtitle, icon }) {
  return (
    <div style={styles.sectionTitle}>
      <div style={styles.sectionIcon}>{icon}</div>
      <div>
        <h2 style={styles.sectionHeading}>{title}</h2>
        <p style={styles.sectionSubtitle}>{subtitle}</p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, inputMode }) {
  return (
    <div>
      <label style={styles.label}>{label}</label>
      <input
        className="editor-input"
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        style={inputStyle}
        inputMode={inputMode}
      />
    </div>
  );
}

function LoadingCard({ text }) {
  return (
    <div style={styles.emptyCard}>
      <div style={styles.spinnerSmall} />
      <div style={{ marginTop: 10, color: COLORS.muted }}>{text}</div>
    </div>
  );
}

function EmptyCard({ text }) {
  return (
    <div style={styles.emptyCard}>
      <div style={styles.emptyIcon}>✓</div>
      <div style={{ color: COLORS.muted }}>{text}</div>
    </div>
  );
}

function CommentModerationModal({ post, onClose, onMessage }) {
  const [comments, setComments] = useState([]);

  useEffect(() => {
    const commentsQuery = query(
      collection(db, 'officialUpdates', post.id, 'comments'),
      orderBy('createdAt', 'asc'),
      limit(200)
    );
    return onSnapshot(commentsQuery, snapshot => {
      setComments(snapshot.docs.map(item => ({ id: item.id, ...item.data() })));
    }, error => onMessage(error?.message || 'Could not load comments.'));
  }, [post.id, onMessage]);

  const removeComment = async comment => {
    try {
      await deleteDoc(doc(db, 'officialUpdates', post.id, 'comments', comment.id));
      await updateDoc(doc(db, 'officialUpdates', post.id), { commentCount: increment(-1) });
    } catch (error) {
      onMessage(error?.message || 'Could not delete comment.');
    }
  };

  const blockCommenter = async comment => {
    if (!comment.uid) return;
    try {
      await setDoc(doc(db, 'users', comment.uid), { commentsBlocked: true }, { merge: true });
      onMessage(`${comment.authorName || comment.uid} can no longer comment.`);
    } catch (error) {
      onMessage(error?.message || 'Could not block this user.');
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={event => event.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div><div style={styles.modalTitle}>Post Comments</div><div style={styles.modalSub}>Delete comments or block commenters</div></div>
          <button onClick={onClose} style={styles.closeButton}>×</button>
        </div>
        <div className="editor-scroll" style={styles.reportDetailList}>
          {comments.length === 0 ? <EmptyCard text="No comments found." /> : comments.map(comment => (
            <div key={comment.id} style={styles.detailCard}>
              <div style={styles.detailTop}><strong>{comment.authorName || comment.uid || 'Unknown user'}</strong><span>{comment.uid || ''}</span></div>
              <div style={styles.detailMessage}>{comment.text}</div>
              <div style={styles.actionRow}>
                <button type="button" onClick={() => removeComment(comment)} style={{ ...buttonStyle, background: 'rgba(255,95,109,.10)', color: COLORS.danger }}>Delete</button>
                <button type="button" onClick={() => blockCommenter(comment)} style={{ ...buttonStyle, background: 'rgba(255,185,70,.10)', color: COLORS.gold }}>Block user</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    height: '100vh',
    background: COLORS.bg,
    color: COLORS.text,
    overflowX: 'hidden',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },

  fullScreen: {
    minHeight: '100vh',
    background: COLORS.bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    color: COLORS.text,
  },

  loaderCard: {
    textAlign: 'center',
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 22,
    padding: 28,
    width: '100%',
    maxWidth: 340,
  },

  deniedCard: {
    width: '100%',
    maxWidth: 420,
    padding: 28,
    borderRadius: 22,
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    textAlign: 'center',
  },

  deniedIcon: {
    width: 54,
    height: 54,
    borderRadius: '50%',
    margin: '0 auto 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,95,109,.12)',
    color: COLORS.danger,
    fontSize: 28,
    fontWeight: 800,
  },

  spinner: {
    width: 30,
    height: 30,
    margin: '0 auto',
    borderRadius: '50%',
    border: '3px solid rgba(255,255,255,.09)',
    borderTopColor: COLORS.green,
    animation: 'editorSpin .75s linear infinite',
  },

  spinnerSmall: {
    width: 24,
    height: 24,
    margin: '0 auto',
    borderRadius: '50%',
    border: '3px solid rgba(255,255,255,.09)',
    borderTopColor: COLORS.green,
    animation: 'editorSpin .75s linear infinite',
  },

  header: {
    position: 'relative',
    zIndex: 20,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    padding: '18px 18px 16px',
    background: 'rgba(5,6,7,.92)',
    backdropFilter: 'blur(16px)',
    borderBottom: `1px solid ${COLORS.border}`,
  },

  brand: {
    fontSize: 27,
    fontWeight: 800,
    letterSpacing: '-.7px',
  },

  subtitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
    color: COLORS.muted,
    fontSize: 13,
  },

  editorBadge: {
    color: COLORS.green,
    border: '1px solid rgba(25,196,99,.25)',
    background: 'rgba(25,196,99,.08)',
    padding: '3px 7px',
    borderRadius: 7,
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '.6px',
  },

  headerUser: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    minWidth: 0,
  },

  avatar: {
    width: 38,
    height: 38,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(25,196,99,.13)',
    color: COLORS.green,
    fontWeight: 800,
    border: '1px solid rgba(25,196,99,.22)',
  },

  headerName: {
    maxWidth: 120,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontWeight: 700,
    fontSize: 13,
  },

  headerRole: {
    color: COLORS.green,
    fontSize: 11,
    marginTop: 2,
  },

  content: {
    width: '100%',
    maxWidth: 900,
    margin: '0 auto',
    padding: 16,
    paddingBottom: 120,
    minHeight: 'calc(100vh - 72px)',
  },

  totalCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background:
      'radial-gradient(circle at 100% 0%, rgba(25,196,99,.18), transparent 48%), #090d0b',
    border: '1px solid rgba(25,196,99,.18)',
    borderRadius: 22,
    padding: '20px 20px',
    boxShadow: '0 15px 45px rgba(0,0,0,.25)',
  },

  eyebrow: {
    color: COLORS.muted,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '1.1px',
  },

  totalNumber: {
    marginTop: 4,
    fontSize: 36,
    lineHeight: 1.1,
    fontWeight: 850,
    letterSpacing: '-1px',
  },

  totalHint: {
    marginTop: 8,
    color: COLORS.muted,
    fontSize: 12,
  },

  code: {
    color: '#9ee9bb',
    background: 'rgba(25,196,99,.08)',
    borderRadius: 5,
    padding: '2px 5px',
  },

  totalIcon: {
    width: 58,
    height: 58,
    borderRadius: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(25,196,99,.10)',
    border: '1px solid rgba(25,196,99,.16)',
    fontSize: 27,
  },

  tabs: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 9,
    marginTop: 14,
  },

  tab: {
    minHeight: 52,
    borderRadius: 15,
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    padding: '9px 8px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 12,
    transition: 'transform .12s ease, background .18s ease, border-color .18s ease',
  },

  tabIcon: {
    fontSize: 16,
  },

  tabCount: {
    minWidth: 20,
    height: 20,
    padding: '0 5px',
    borderRadius: 10,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,.05)',
    fontSize: 11,
  },

  section: {
    marginTop: 18,
  },

  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 14,
  },

  sectionIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(25,196,99,.09)',
    border: '1px solid rgba(25,196,99,.15)',
    color: COLORS.green,
    fontSize: 20,
    fontWeight: 800,
  },

  sectionHeading: {
    margin: 0,
    fontSize: 19,
    letterSpacing: '-.3px',
  },

  sectionSubtitle: {
    margin: '4px 0 0',
    color: COLORS.muted,
    fontSize: 12,
    lineHeight: 1.45,
  },

  searchRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: 9,
  },

  messageBox: {
    marginTop: 10,
    padding: '10px 12px',
    borderRadius: 12,
    background: 'rgba(25,196,99,.07)',
    border: '1px solid rgba(25,196,99,.12)',
    color: '#bceecf',
    fontSize: 13,
  },

  emptyCard: {
    marginTop: 14,
    textAlign: 'center',
    padding: '30px 18px',
    borderRadius: 20,
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
  },

  emptyIcon: {
    width: 42,
    height: 42,
    borderRadius: '50%',
    margin: '0 auto 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,.05)',
    color: COLORS.muted,
    fontSize: 21,
  },

  userCard: {
    marginTop: 14,
    padding: 16,
    borderRadius: 21,
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
  },

  userCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 18,
  },

  profileBlock: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },

  profileImage: {
    width: 54,
    height: 54,
    borderRadius: '50%',
    objectFit: 'cover',
    background: '#161a1f',
    border: '1px solid rgba(255,255,255,.08)',
  },

  profileName: {
    fontWeight: 800,
    fontSize: 16,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  profileNumber: {
    marginTop: 4,
    color: COLORS.muted,
    fontSize: 12,
  },

  statusPill: {
    flexShrink: 0,
    border: '1px solid',
    background: 'rgba(255,255,255,.03)',
    padding: '6px 9px',
    borderRadius: 9,
    fontSize: 11,
    fontWeight: 800,
  },

  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
  },

  permissionCard: {
    gridColumn: '1 / -1',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: 13,
    borderRadius: 12,
    border: `1px solid ${COLORS.border}`,
    background: COLORS.card2,
    cursor: 'pointer',
  },

  label: {
    display: 'block',
    marginBottom: 7,
    color: COLORS.muted,
    fontSize: 12,
    fontWeight: 700,
  },

  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 9,
    marginTop: 16,
  },

  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },

  reportRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 13,
    background: COLORS.card,
    color: COLORS.text,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 17,
    cursor: 'pointer',
  },

  reportAvatar: {
    width: 43,
    height: 43,
    flexShrink: 0,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(25,196,99,.09)',
    color: COLORS.green,
    fontWeight: 800,
  },

  reportName: {
    fontWeight: 750,
    fontSize: 14,
  },

  reportUid: {
    color: COLORS.muted,
    marginTop: 3,
    fontSize: 11,
  },

  reportCount: {
    minWidth: 34,
    height: 34,
    padding: '0 8px',
    borderRadius: 17,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,95,109,.10)',
    color: COLORS.danger,
    fontWeight: 800,
  },

  problemTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 18,
    borderRadius: 20,
    background: 'rgba(255,95,109,.055)',
    border: '1px solid rgba(255,95,109,.12)',
  },

  problemNumber: {
    marginTop: 4,
    fontSize: 32,
    fontWeight: 850,
  },

  problemIcon: {
    width: 52,
    height: 52,
    borderRadius: 17,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.danger,
    background: 'rgba(255,95,109,.10)',
    fontSize: 24,
    fontWeight: 900,
  },

  problemRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 11,
    padding: 14,
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 17,
  },

  problemAvatar: {
    width: 40,
    height: 40,
    flexShrink: 0,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,95,109,.09)',
    color: COLORS.danger,
    fontWeight: 800,
  },

  problemMessage: {
    marginTop: 5,
    color: '#c6cdd6',
    fontSize: 13,
    lineHeight: 1.45,
  },

  problemDate: {
    marginTop: 6,
    color: COLORS.muted,
    fontSize: 10,
  },

  officialCard: {
    padding: 17,
    borderRadius: 21,
    background:
      'radial-gradient(circle at 100% 0%, rgba(25,196,99,.12), transparent 44%), #090d0b',
    border: '1px solid rgba(25,196,99,.15)',
  },

  officialHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },

  officialImage: {
    width: 55,
    height: 55,
    borderRadius: '50%',
    objectFit: 'cover',
    background: '#151a18',
    border: '2px solid rgba(25,196,99,.22)',
  },

  officialEditButton: {
    flexShrink: 0,
    border: '1px solid rgba(25,196,99,.24)',
    borderRadius: 11,
    padding: '8px 11px',
    background: 'rgba(25,196,99,.08)',
    color: COLORS.green,
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 12,
  },

  officialProfileEditor: {
    marginTop: 14,
    padding: 14,
    borderRadius: 16,
    background: 'rgba(0,0,0,.22)',
    border: '1px solid rgba(25,196,99,.16)',
  },

  officialProfileEditorTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: COLORS.text,
    marginBottom: 12,
  },

  officialProfilePreviewRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 14,
  },

  officialProfilePreview: {
    width: 62,
    height: 62,
    flexShrink: 0,
    borderRadius: '50%',
    objectFit: 'cover',
    background: '#151a18',
    border: '2px solid rgba(25,196,99,.22)',
  },

  officialProfileActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 9,
    marginTop: 14,
  },

  officialName: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    fontWeight: 800,
    fontSize: 17,
  },

  verified: {
    width: 19,
    height: 19,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: COLORS.green,
    color: '#001b0b',
    fontSize: 12,
    fontWeight: 900,
  },

  officialMeta: {
    marginTop: 4,
    color: COLORS.muted,
    fontSize: 12,
  },

  officialBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginTop: 12,
    flexWrap: 'wrap',
  },

  composerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
    marginTop: 18,
  },

  permissionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
    marginTop: 14,
  },

  inlineFields: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.5fr)',
    gap: 8,
  },

  videoControls: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 18,
    flexWrap: 'wrap',
    marginTop: 14,
    padding: 13,
    borderRadius: 15,
    background: 'rgba(255,255,255,.025)',
    border: `1px solid ${COLORS.border}`,
  },

  checkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#d7dee7',
    fontSize: 13,
    paddingTop: 4,
  },

  apkBox: {
    marginTop: 16,
    padding: 14,
    borderRadius: 16,
    background: 'rgba(74,168,255,.055)',
    border: '1px solid rgba(74,168,255,.12)',
  },

  postHistoryCard: {
    marginTop: 16,
    padding: 17,
    borderRadius: 21,
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
  },

  historyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 13,
  },

  cardTitle: {
    fontSize: 15,
    fontWeight: 800,
  },

  historyHint: {
    marginTop: 4,
    color: COLORS.muted,
    fontSize: 11,
  },

  officialPostRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    borderRadius: 16,
    background: '#080b0e',
    border: `1px solid ${COLORS.border}`,
  },

  postPermissions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    flexShrink: 0,
    color: COLORS.muted,
    fontSize: 10,
  },

  postRowTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    fontSize: 12,
  },

  postRowText: {
    marginTop: 5,
    color: '#dce3eb',
    fontSize: 13,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  postRowMeta: {
    marginTop: 5,
    color: COLORS.muted,
    fontSize: 10,
    lineHeight: 1.4,
  },

  viewCountBox: {
    minWidth: 62,
    padding: '7px 8px',
    borderRadius: 12,
    background: 'rgba(25,196,99,.07)',
    border: '1px solid rgba(25,196,99,.12)',
    textAlign: 'center',
  },

  viewNumber: {
    color: COLORS.green,
    fontSize: 15,
    fontWeight: 850,
  },

  viewLabel: {
    marginTop: 2,
    color: COLORS.muted,
    fontSize: 9,
  },

  modalOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 100,
    background: 'rgba(0,0,0,.72)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 18,
  },

  modal: {
    width: '100%',
    maxWidth: 620,
    maxHeight: '82vh',
    overflow: 'hidden',
    background: '#080b0e',
    border: `1px solid ${COLORS.border}`,
    borderRadius: 22,
    boxShadow: '0 25px 80px rgba(0,0,0,.5)',
  },

  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    padding: 17,
    borderBottom: `1px solid ${COLORS.border}`,
  },

  modalTitle: {
    fontWeight: 800,
    fontSize: 17,
  },

  modalSub: {
    color: COLORS.muted,
    fontSize: 12,
    marginTop: 4,
  },

  closeButton: {
    width: 35,
    height: 35,
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,.08)',
    background: 'rgba(255,255,255,.05)',
    color: COLORS.text,
    fontSize: 23,
    cursor: 'pointer',
    lineHeight: 1,
  },

  reportDetailList: {
    maxHeight: 'calc(82vh - 80px)',
    overflowY: 'auto',
    padding: 14,
  },

  detailCard: {
    padding: 13,
    borderRadius: 15,
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    marginBottom: 8,
  },

  detailTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    color: COLORS.muted,
    fontSize: 11,
  },

  detailMessage: {
    marginTop: 8,
    color: '#d8dee6',
    fontSize: 13,
    lineHeight: 1.5,
  },
};

/*
  Small global animation rule.
  It is injected only while Editor.js is mounted.
*/
if (typeof document !== 'undefined' && !document.getElementById('editor-spin-style')) {
  const style = document.createElement('style');
  style.id = 'editor-spin-style';
  style.textContent = `
    @keyframes editorSpin {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}
